import { CloseOutlined } from '@ant-design/icons';
import { Heading } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { invitesCRUDActions } from '@mm/state/actions/InvitesCRUDActions';
import { BasicPage } from '@mm/state/interfaces/connected-page';
import CompanyModel from '@mm/state/model/CompanyModel';
import {
  isLiveChatAgentOrFacebookPageCollaborator,
  NewUserRole,
  UserRole,
} from '@mm/state/model/CompanyUsersModel';
import InviteModel from '@mm/state/model/InviteModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import {
  enhancedBillingPagesSelector,
  selectAvailablePages,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { notification } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import { FacebookPagesAccess } from '../../components/FacebookPagesAccess/FacebookPagesAccess';
import { UserRoleSelector } from '../../components/UserRoleSelector/UserRoleSelector';
import { PAGES_UPGRADE_COUNT } from '../../constants/payments';
import { ModalProcessingBlinder } from '../modal-helpers';
import { EmailInput, Payload } from './EmailInput/EmailInput';
import './InviteUserModal.pcss';

type Props = {
  company: CompanyModel;
  availableSeats: number[];
  className?: string;
  closeModal: Function;
};

type Invite = Partial<InviteModel>;

const rolesWithBadgesMap: { [key: string]: boolean } = {
  [UserRole.Admin]: true,
  [UserRole.Collaborator]: true,
  [NewUserRole.Admin]: true,
  [NewUserRole.Collaborator]: true,
};
const rolesWithoutBadgesMap: { [key: string]: boolean } = {
  [UserRole.FacebookPageCollaborator]: true,
  [UserRole.LiveChatAgent]: true,
  [NewUserRole.FacebookPageCollaborator]: true,
  [NewUserRole.LiveChatAgent]: true,
};

const useCreateEmailInvite = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const dispatch: Dispatch<StoreState> = useDispatch();

  const createAndSendInvite = async (company: CompanyModel, invite: Invite) => {
    setIsLoading(true);
    const createdInvite = await dispatch(
      invitesCRUDActions.create(company.id, {
        ...invite,
        active: true,
      }),
    );
    if (createdInvite && createdInvite.active) {
      setIsSuccess(true);
    }
    setIsLoading(false);
  };

  return {
    createAndSendInvite,
    isLoading,
    isSuccess,
  };
};

export const InviteUserModal = ({
  className,
  closeModal,
  company,
  availableSeats,
}: Props) => {
  const availableSeatsMap = useMemo(
    () =>
      availableSeats.reduce((acc: { [key: number]: boolean }, curr: number) => {
        acc[curr] = true;
        return acc;
      }, {}),
    [availableSeats],
  );
  const pages =
    useSelector(enhancedBillingPagesSelector).map(page => ({
      ...page,
      disabled: !availableSeatsMap[page.id],
    })) || [];

  const hasTeamSubscription = company.teamPricePlan;

  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const availablePages = useSelector(selectAvailablePages);
  const needsUpgrade =
    company?.editable &&
    availablePages?.data.length > PAGES_UPGRADE_COUNT &&
    !featureFlags.connectPageUnlimitedPages;
  const dispatch: Dispatch<StoreState> = useDispatch();

  const [userRole, setUserRole] = useState<string>(
    UserRole.FacebookPageCollaborator,
  );
  const [selectedPages, setSelectedPages] = useState<BasicPage[]>([]);
  const [emailState, setEmailState] = useState({ isValid: false, email: '' });

  const { createAndSendInvite, isLoading, isSuccess } = useCreateEmailInvite();

  const onEmailChange = ({ isValid, email }: Payload) =>
    setEmailState({ isValid, email });
  const onUserRoleChange = (role: string) => setUserRole(role);
  const onPageSelect = (pages: BasicPage[]) => setSelectedPages(pages);

  const onSendInviteBtnClick = async () => {
    if (rolesWithoutBadgesMap[userRole]) {
      await createAndSendInvite(company, {
        allowedFacebookPageIds: selectedPages.map(({ id }) => id),
        email: emailState.email,
        role: userRole,
      });
      return;
    }
    if (
      needsUpgrade ||
      (!featureFlags.userInvitesEditorAndAdmin && rolesWithBadgesMap[userRole])
    ) {
      dispatch(
        modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['INVITE-USER-MODAL'],
          feature: 'userInvitesEditorAndAdmin',
        }),
      );
      return;
    }

    await createAndSendInvite(company, {
      allowedFacebookPageIds: selectedPages.map(({ id }) => id),
      email: emailState.email,
      role: userRole,
    });
  };

  const showPageSelector = isLiveChatAgentOrFacebookPageCollaborator(
    userRole as string,
  );

  const isInviteBtnDisabled =
    !emailState.isValid ||
    !userRole ||
    isLoading ||
    (isLiveChatAgentOrFacebookPageCollaborator(userRole) &&
      !selectedPages.length);

  useEffect(() => {
    if (isSuccess) {
      notification.success({
        message: 'Invite sent to:',
        description: `${emailState.email}`,
        duration: 5,
      });
      closeModal();
    }
  }, [isSuccess]);

  return (
    <div className={classNames(className, 'invite-user-modal')}>
      <CloseOutlined
        className="user-editor-component__close-icon"
        onClick={() => closeModal()}
      />
      <div className="user-editor-component__header-content">
        Invite New User
      </div>

      <div className="invite-user-modal__inputs">
        <div className="invite-user-modal__input-container">
          <Heading
            className="invite-user-modal__input-label"
            size={5}
            isBold={false}
          >
            Email
          </Heading>
          <EmailInput
            className="invite-user-modal__email-input"
            onChange={onEmailChange}
            withIcon={false}
          />
        </div>

        <span className="invite-user-modal__filler" />
        <div className="invite-user-modal__input-container">
          <Heading
            className="invite-user-modal__input-label"
            size={5}
            isBold={false}
          >
            Role
          </Heading>
          <UserRoleSelector
            className="invite-user-modal__user-role-selector"
            placeholder="User role"
            onUpdate={onUserRoleChange}
            value={userRole}
            showTeamBagde={!hasTeamSubscription}
          />
        </div>
      </div>
      <div className="user-editor-component__facebook-pages">
        {showPageSelector && (
          <FacebookPagesAccess
            pages={pages as BasicPage[]}
            selectedPages={selectedPages}
            onPagesSelected={onPageSelect}
            disabled={false}
          />
        )}
      </div>

      <div className="user-editor-component__header-buttons">
        <PrimaryButton
          className="user-editor-component__cancel-button"
          onClick={() => closeModal()}
          color="transparent-no-shadow"
        >
          Cancel
        </PrimaryButton>
        <PrimaryButton
          className="user-editor-component__button"
          disabled={isInviteBtnDisabled}
          onClick={onSendInviteBtnClick}
        >
          Send Invite
        </PrimaryButton>
      </div>
      <ModalProcessingBlinder show={isLoading} />
    </div>
  );
};
